import { LoadingSpinner } from "components/LoadingSpinner";
import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import useFormValidation from "hooks/useFormValidation";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { isEmpty } from "lodash";
import { handleChangeDateWithMask } from "pages/CreateOrder/utils/mask";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";
import {
  IOrder,
  IGFEServiceDetails,
  IFacilityRules,
  INonFacilityRules,
} from "types/Order/Order";
import { IProvider } from "types/Provider/Provider";

import { Benefits } from "./Benefits";
import { EligibilityButton } from "./EligibilityButton";

const StyledEligibilityButtonContainer = styled.div`
  @media (min-width: 992px) {
    margin-top: 28px;
  }
`;

const StyledMessageContainer = styled.div`
  border: 1px solid var(--gray-new-200);
  border-left: none;
  border-right: none;
  background-color: #f9fafb;
  padding: 22px 24px;
  color: #dc6803;
  display: flex;
  align-items: center;
  gap: ${sizer(2)};
`;

export function PrimaryInsuranceSection({
  loadingPayersName,
  payersNameInfo,
  payersCodesInfo,
  redo = false,
  draft = false,
  copy = false,
  isEligibilityEnabledForMainProvider,
  accountId,
  mainProvider,
  costApplicationRules,
  account,
  draftOrder,
  orderToRedo,
  orderToCopy,
}: {
  loadingPayersName: boolean;
  payersNameInfo: any[];
  payersCodesInfo: any[];
  redo?: boolean;
  draft?: boolean;
  copy?: boolean;
  isEligibilityEnabledForMainProvider?: boolean;
  accountId: string;
  mainProvider: IProvider;
  costApplicationRules?: {
    facilityRules: IFacilityRules;
    nonFacilityRules: INonFacilityRules;
  };
  account: IAccount;
  draftOrder?: IOrder;
  orderToRedo?: IOrder;
  orderToCopy?: IOrder;
}) {
  const {
    control,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [eligibilityMessage, setEligibilityMessage] = useState<string | null>(
    null
  );
  const [showMemberIdAndEligibility, setShowMemberIdAndEligibility] =
    useState<boolean>(true);

  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();

  const payerNameField = watch("payerName");
  const payerField = watch("payer");
  const patientFirstNameField = watch("firstName");
  const patientLastNameField = watch("lastName");
  const patientDOBField = watch("dateOfBirth");
  const memberIdField = watch("memberId");
  const practiceTypeField = watch("practiceType");
  const isSubscriberPatientCheckbox = watch("isSubscriberPatient");
  const benefitDetailsField = watch("benefitDetails");

  useEffect(() => {
    if ((payerField?.name && payerField?.payerId) || loadingPayersName) return;
    const applyPayer = (payerName: string, payerId: number) => {
      setValue("payerName", payerName);
      setValue("payer", { name: payerName, payerId });
    };

    const getPayerFromServiceDetails = (payerName?: string) => {
      if (!payerName && isEmpty(payersNameInfo)) {
        return { name: null, payerId: null };
      }
      const name = payerName || payersNameInfo[0]?.value?.name;
      const payerId =
        payerName &&
        payersNameInfo?.find((payer: any) => payer.name === payerName)?.value
          .payerId
          ? payersNameInfo.find((payer: any) => payer.name === payerName)?.value
              .payerId
          : payersNameInfo[0].value.payerId;

      return { name, payerId };
    };

    const processOrder = (order: any) => {
      const savedPayerId = order?.payerId;
      if (savedPayerId) {
        applyPayer(order?.payer?.name, order?.payer?.id);
      } else {
        const payerNameFromDetails = (
          order?.serviceDetails as IGFEServiceDetails
        )?.payerName;
        const { name, payerId } =
          getPayerFromServiceDetails(payerNameFromDetails);
        if (!name && !payerId) {
          return;
        }
        applyPayer(name, payerId);
      }
    };
    if (redo) {
      processOrder(orderToRedo);
    } else if (copy) {
      processOrder(orderToCopy);
    } else if (draft) {
      processOrder(draftOrder);
    } else {
      applyPayer(
        payersNameInfo[0]?.value?.name,
        payersNameInfo[0]?.value?.payerId
      );
    }
  }, [payerField, payersNameInfo, redo, copy, draft]);

  useEffect(() => {
    if (payerNameField === "Self-Pay Rate") {
      setShowMemberIdAndEligibility(false);
      setValue("deductible", 0);
      setValue("outOfPocketMax", 100000);
      setValue("flatCopay", 0);
      setValue("coinsurancePercent", 100);
    } else {
      setShowMemberIdAndEligibility(true);
    }
  }, [payerNameField]);

  // Set eligibility message based on stored benefitDetails planStatus
  // NOTE: Don't clear the message if recommendedPayerBasedOnEligibility is present,
  // as that message is set by EligibilityButton and should persist (CLE-1221).
  useEffect(() => {
    const response = benefitDetailsField?.response;
    if (!response) {
      return;
    }
    // If we detected Medicare Advantage and recommended a payer swap, don't overwrite that message
    if (response.recommendedPayerBasedOnEligibility) {
      return;
    }
    if (response.planStatus === "Inactive") {
      setEligibilityMessage(
        "The patient coverage shows inactive, please contact the patient or the payer to obtain most current information"
      );
    } else {
      const allFieldsAreNull =
        response.familyCalendarYearDeductible === null &&
        response.individualCalendarYearDeductible === null &&
        response.remainingDeductible === null &&
        response.coinsurancePercent === null;
      if (response.isEligibilityEmpty || allFieldsAreNull) {
        setEligibilityMessage(response.additionalInfo);
      } else {
        setEligibilityMessage(null);
      }
    }
  }, [benefitDetailsField]);

  useEffect(() => {
    const draftOrderCheck =
      draft &&
      (!draftOrder?.benefitDetails || !draftOrder?.benefitDetails?.response);
    const redoOrderCheck =
      redo &&
      (!orderToRedo?.benefitDetails || !orderToRedo?.benefitDetails?.response);
    const orderToCopyCheck =
      copy &&
      (!orderToCopy?.benefitDetails || !orderToCopy?.benefitDetails?.response);
    if (
      loadingPayersName ||
      draftOrderCheck ||
      redoOrderCheck ||
      orderToCopyCheck ||
      (!draft && !redo && !copy)
    ) {
      setValue("benefitDetails", null);
      return;
    }
    let order = orderToRedo;
    if (draft) {
      order = draftOrder;
    } else if (copy) {
      order = orderToCopy;
    }
    const providerNpi = mainProvider?.npi;
    const eligibilityEnabled = mainProvider?.eligibilityEnabled;
    const eligibilityRequest = order?.benefitDetails?.request;
    if (
      eligibilityRequest?.member?.firstName !== patientFirstNameField ||
      eligibilityRequest?.member?.lastName !== patientLastNameField ||
      eligibilityRequest?.member?.dob !== patientDOBField ||
      eligibilityRequest?.member?.memberID !== memberIdField ||
      eligibilityRequest?.practiceTypeCode !== practiceTypeField ||
      eligibilityRequest?.provider?.npi !== providerNpi ||
      eligibilityRequest?.isSubscriberPatient !== eligibilityEnabled
    ) {
      setValue("benefitDetails", null);
    } else {
      setValue("benefitDetails", order?.benefitDetails);
      if (redo || draft || copy) {
        const response = order?.benefitDetails?.response;
        if (response.planStatus === "Inactive") {
          setEligibilityMessage(
            "The patient coverage shows inactive, please contact the patient or the payer to obtain most current information"
          );
        }
        const allFieldsAreNull =
          response.familyCalendarYearDeductible === null &&
          response.individualCalendarYearDeductible === null &&
          response.remainingDeductible === null &&
          response.coinsurancePercent === null;
        if (response.isEligibilityEmpty || allFieldsAreNull) {
          setEligibilityMessage(response.additionalInfo);
        }
      }
    }
  }, [
    patientFirstNameField,
    patientLastNameField,
    patientDOBField,
    memberIdField,
    practiceTypeField,
    loadingPayersName,
    payersCodesInfo,
    draftOrder,
    orderToRedo,
    orderToCopy,
    mainProvider,
  ]);

  return (
    <>
      <div className="formgrid grid flex-column lg:flex-row px-3">
        <div className="field flex flex-column lg:col-9 col-12">
          <div className="formgrid grid">
            <div className="field flex flex-column col-12 lg:col-4">
              <Label htmlFor="payer">Payer Name</Label>

              <Controller
                control={control}
                name="payer"
                render={({ field: { onChange, value } }) =>
                  loadingPayersName ? (
                    <LoadingSpinner
                      customStyle={{ width: "50px", height: "100px" }}
                    />
                  ) : (
                    <Dropdown
                      value={value || ""}
                      optionLabel="name"
                      placeholder="Any"
                      id="payer"
                      data-testid="payer_PayerForm"
                      options={payersNameInfo}
                      onChange={(e) => {
                        onChange(e.value);
                        setValue("payerName", e.value.name);
                        if (redo) {
                          setValue("redoOrderFirstRender", false);
                        }
                        if (draft) {
                          setValue("draftOrderFirstRender", false);
                        }
                        if (copy) {
                          setValue("copyOrderFirstRender", false);
                        }
                      }}
                    />
                  )
                }
              />
            </div>
            {isEligibilityEnabledForMainProvider && (
              <div className="field flex flex-column col-12 lg:col-4">
                <Label htmlFor="practiceType">Service Type</Label>

                <Controller
                  control={control}
                  name="practiceType"
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      value={value || ""}
                      optionLabel="name"
                      data-testid="practiceType_PayerForm"
                      placeholder="Service Type"
                      id="practiceType"
                      options={mapPayerNameToCodeForDropdown}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
            )}
            {isEligibilityEnabledForMainProvider &&
              showMemberIdAndEligibility && (
                <div className="field flex flex-column col-12 lg:col-4">
                  <Label htmlFor="memberId">Member ID</Label>

                  <Controller
                    control={control}
                    name="memberId"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <>
                        <InputText
                          id="memberId"
                          value={value || ""}
                          autoComplete="off"
                          data-testid="memberId_PayerForm"
                          placeholder="Member ID"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "memberId",
                              trigger,
                            })
                          }
                          onChange={onChange}
                        />

                        {getFormErrorMessage("memberId", errors)}
                      </>
                    )}
                  />
                </div>
              )}
          </div>
          {isEligibilityEnabledForMainProvider &&
            !isSubscriberPatientCheckbox && (
              <div className="formgrid grid">
                <div className="field flex flex-column col-12 lg:col-4">
                  <Label htmlFor="subscriberFirstName">
                    Subscriber First Name
                  </Label>

                  <Controller
                    control={control}
                    name="subscriberFirstName"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <>
                        <InputText
                          id="subscriberFirstName"
                          data-testid="subscriberFirstName_PayerForm"
                          value={value || ""}
                          autoComplete="off"
                          placeholder="Subscriber Name"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "subscriberFirstName",
                              trigger,
                            })
                          }
                          onChange={onChange}
                        />

                        {getFormErrorMessage("subscriberFirstName", errors)}
                      </>
                    )}
                  />
                </div>
                <div className="field flex flex-column col-12 lg:col-4">
                  <Label htmlFor="subscriberLastName">
                    Subscriber Last Name
                  </Label>

                  <Controller
                    control={control}
                    name="subscriberLastName"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <>
                        <InputText
                          id="subscriberLastName"
                          data-testid="subscriberLastName_PayerForm"
                          value={value || ""}
                          autoComplete="off"
                          placeholder="Subscriber Name"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "subscriberLastName",
                              trigger,
                            })
                          }
                          onChange={onChange}
                        />

                        {getFormErrorMessage("subscriberLastName", errors)}
                      </>
                    )}
                  />
                </div>
                <div className="field flex flex-column col-12 lg:col-4">
                  <Label htmlFor="subscriberDOB">Subscriber DOB</Label>

                  <Controller
                    name="subscriberDOB"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <>
                        <InputText
                          value={value || ""}
                          id="subscriberDOB"
                          data-testid="subscriberDOB_PayerForm"
                          placeholder="99/99/9999"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "subscriberDOB",
                              trigger,
                            })
                          }
                          onChange={(e) =>
                            handleChangeDateWithMask(e, onChange)
                          }
                        />

                        {getFormErrorMessage("subscriberDOB", errors)}
                      </>
                    )}
                  />
                </div>
              </div>
            )}
          {isEligibilityEnabledForMainProvider && (
            <div className="field flex-column col-12 md:col-12 lg:col-3 hidden">
              <Label htmlFor="groupNumber">Group Number</Label>

              <Controller
                control={control}
                name="groupNumber"
                render={({ field: { onChange, value, onBlur } }) => (
                  <>
                    <InputText
                      id="groupNumber"
                      data-testid="groupNumber_PayerForm"
                      value={value || ""}
                      autoComplete="off"
                      placeholder="Group Number"
                      onBlur={() =>
                        handleOnBlurField({
                          onBlur,
                          field: "groupNumber",
                          trigger,
                        })
                      }
                      onChange={onChange}
                    />

                    {getFormErrorMessage("groupNumber", errors)}
                  </>
                )}
              />
            </div>
          )}
        </div>
        <StyledEligibilityButtonContainer className="field flex col-3">
          {showMemberIdAndEligibility ? (
            <EligibilityButton
              accountId={accountId}
              mainProvider={mainProvider}
              errorMessageSetter={setEligibilityMessage}
              disabled={loadingPayersName}
            />
          ) : null}
        </StyledEligibilityButtonContainer>
      </div>
      <StyledMessageContainer>
        <i className="pi pi-info-circle" style={{ fontSize: "20px" }} />
        <p className="text-md font-semibold">
          Confirm benefits are correct by checking all location-specific options
        </p>
      </StyledMessageContainer>
      <Benefits
        redo={redo}
        draft={draft}
        copy={copy}
        costApplicationRules={costApplicationRules}
        account={account}
        eligibilityMessage={eligibilityMessage}
      />
    </>
  );
}
